/**
 * §A.3 · Soporte: quién es esta persona y qué le pasa.
 *
 * **Buscar por correo es lo primero que hace soporte cuando llaman.** No «buscar
 * el club y luego la persona»: quien llama dice su correo, y de ahí sale todo lo
 * demás. Por eso la búsqueda es la pantalla y no un campo escondido.
 *
 * ## Las dos respuestas que se dan más veces
 *
 * `email_verified` contesta «no me llega el correo» y «no puedo entrar», y hasta
 * que existió esta pantalla había que abrir la base de datos para verlo. Va arriba
 * y con su color.
 *
 * ## Entrar en un club deja rastro, y aquí se ve
 *
 * `kiap_platform` es `BYPASSRLS`: desde el back office se ven los datos de
 * cualquier club. Lo que hace eso defendible son las tres cosas juntas —motivo
 * obligatorio, caducidad y **aviso al club**— y que las entradas se puedan revisar
 * después. La lista de abajo es esa revisión: si no se mira nunca, el rastro no
 * sirve de nada.
 *
 * `active` lo resuelve el servidor y **no se deduce aquí**: no es «no tiene
 * `ended_at`», es eso y que no haya caducado. Deducirlo en el cliente es cómo una
 * pantalla de auditoría acaba enseñando siete accesos vivos que no lo están.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import { recordarEntrada } from '@/shared/api/soporte';
import { useYo } from '@/shared/auth/sesionContexto';
import type {
  ClubResumen,
  Persona,
  PersonaDetalle,
  SesionDeSoporte,
} from '@/backoffice/tipos';

/** Lo que hay que escribir para entrar: el club y el motivo. */
interface Entrando {
  readonly club: string;
  readonly motivo: string;
}

/**
 * Los clubes en los que tiene sentido entrar: los que tienen a alguien detrás. Los
 * del censo (`prospect`) no tienen panel que mirar, y los que se fueron tampoco.
 */
const ESTADOS_CON_PANEL = ['trial', 'active', 'suspended'] as const;

export function Soporte({
  clubParaSoporte,
  alTerminarConElClub,
  personaAbierta,
  alAbrirPersona,
}: {
  /** Llega desde la ficha de un club: «entrar en modo soporte». */
  clubParaSoporte?: string | undefined;
  alTerminarConElClub: () => void;
  personaAbierta?: string | undefined;
  alAbrirPersona: (userId: string | null) => void;
}) {
  const cache = useQueryClient();
  const yo = useYo();
  const ir = useNavigate();
  const [busqueda, setBusqueda] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);
  /*
   * El formulario de entrar, abierto o no. Se llega desde cuatro sitios: el botón de
   * arriba de esta pantalla, la ficha del club, cada club de una persona y «Volver a
   * entrar» en una entrada tuya. El de la ficha llega por la propiedad, y se siembra
   * durante el render y no en un efecto, como el resto del panel.
   */
  const [entrando, setEntrando] = useState<Entrando | null>(null);
  const [pedidoDesdeLaFicha, setPedidoDesdeLaFicha] = useState<
    string | undefined
  >(undefined);
  if (clubParaSoporte && clubParaSoporte !== pedidoDesdeLaFicha) {
    setPedidoDesdeLaFicha(clubParaSoporte);
    setEntrando({ club: clubParaSoporte, motivo: '' });
  }

  const abrirEntrada = (club: string, motivo = '') => {
    setProblema(null);
    setEntrando({ club, motivo });
  };

  const cerrarEntrada = () => {
    setEntrando(null);
    setPedidoDesdeLaFicha(undefined);
    setProblema(null);
    alTerminarConElClub();
  };

  const personas = useQuery({
    queryKey: ['admin', 'personas', busqueda],
    // Sin texto no se pide nada: una lista de todos los usuarios de la plataforma
    // no contesta ninguna pregunta y expone correos sin motivo.
    enabled: busqueda.trim().length >= 3,
    queryFn: async () => {
      const { data, error } = await api.v1.admin.users.get({
        query: { q: busqueda.trim(), limit: 25 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Persona[];
    },
  });

  const persona = useQuery({
    queryKey: ['admin', 'persona', personaAbierta],
    enabled: Boolean(personaAbierta),
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .users({ id: personaAbierta! })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PersonaDetalle;
    },
  });

  const sesiones = useQuery({
    queryKey: ['admin', 'soporte'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['support-sessions'].get({
        query: { limit: 25 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SesionDeSoporte[];
    },
  });

  /*
   * Los clubes que se pueden elegir. Un estado por consulta, porque la lista del back
   * office trae también las fichas del censo y con ellas los reales se saldrían del
   * tope de una sola página.
   */
  const clubes = useQuery({
    queryKey: ['admin', 'clubes', 'para-soporte'],
    enabled: entrando !== null,
    queryFn: async () => {
      const paginas = await Promise.all(
        ESTADOS_CON_PANEL.map(async (status) => {
          const { data, error } = await api.v1.admin.academies.get({
            query: { limit: 200, status },
          });
          if (error) throw new ErrorDeApi(aProblema(error));
          return data as unknown as ClubResumen[];
        })
      );
      return paginas
        .flat()
        .sort((a, b) => a.trade_name.localeCompare(b.trade_name, 'es'));
    },
  });

  /**
   * Abre el panel del club con esta entrada, en esta misma pestaña. Es la misma
   * dirección del back office: las rutas del panel caben al lado de `/admin`, y la
   * entrada recordada es lo que decide que se monte en modo soporte.
   */
  const abrirSuPanel = (id: string) => {
    recordarEntrada(id);
    void ir({ to: '/inicio' });
  };

  const entrar = useMutation({
    mutationFn: async (e: Entrando) => {
      const r = await api.v1.admin['support-sessions'].post(
        { academy_id: e.club, reason: e.motivo.trim() },
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as SesionDeSoporte;
    },
    // Entrar es para mirar el club: se abre su panel directamente.
    onSuccess: (sesion) => {
      cerrarEntrada();
      void cache.invalidateQueries({ queryKey: ['admin', 'soporte'] });
      abrirSuPanel(sesion.id);
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const salir = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.admin['support-sessions']({ id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () =>
      void cache.invalidateQueries({ queryKey: ['admin', 'soporte'] }),
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Abrir modo soporte sobre un club ─────────────────────────────────────
  if (entrando) {
    const elegido = clubes.data?.find((c) => c.id === entrando.club);
    // Ya dentro de ese club: abrir otra daría «ya estás dentro». Se ofrece la que hay.
    const yaDentro = sesiones.data?.find(
      (x) =>
        x.active &&
        x.academy_id === entrando.club &&
        x.staff_user_id === yo.user.id
    );

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          entrar.mutate(entrando);
        }}
      >
        <h1>
          Entrar en modo soporte
          {elegido ? ` en ${elegido.trade_name}` : ''}
        </h1>

        <ul className="lista-clara">
          <li>
            <strong>El club recibe un aviso</strong> con este motivo escrito. Lo
            va a leer, así que se escribe para que lo lea.
          </li>
          <li>
            <strong>Caduca sola</strong> a la media hora. Si hace falta más, se
            vuelve a entrar y el club recibe otro aviso: renovar en silencio le
            quitaría el sentido.
          </li>
          <li>
            <strong>Se abre su panel tal como lo ve el dueño</strong>, en solo
            lectura. Para cambiar algo, pulsa «Entrar en modo escritura» en la
            banda de arriba, y cada cambio te pedirá confirmación. Los chats no
            se abren nunca.
          </li>
          <li>
            <strong>Queda en el registro</strong>, cada pantalla que mires y
            cada cambio, y el club lo ve en el suyo.
          </li>
        </ul>

        <label>
          En qué club
          <select
            required
            className="selector-de-club"
            value={entrando.club}
            onChange={(e) => setEntrando({ ...entrando, club: e.target.value })}
          >
            <option value="">
              {clubes.isPending ? 'Cargando los clubes…' : 'Elige el club'}
            </option>
            {/* El de la ficha, aunque la lista aún no haya llegado. */}
            {entrando.club !== '' && !elegido && (
              <option value={entrando.club}>El club de la ficha</option>
            )}
            {clubes.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.trade_name}
                {c.is_demo ? ' (demo)' : ''}
                {c.status === 'suspended' ? ' · suspendido' : ''}
              </option>
            ))}
          </select>
        </label>

        <label>
          Por qué entras
          <input
            required
            minLength={10}
            maxLength={300}
            // Con el club ya elegido, lo que queda es escribir el motivo.
            autoFocus={entrando.club !== ''}
            placeholder="Nos han llamado porque no les sale el recibo de septiembre"
            value={entrando.motivo}
            onChange={(e) =>
              setEntrando({ ...entrando, motivo: e.target.value })
            }
          />
        </label>

        {yaDentro ? (
          <div className="nota">
            <strong>Ya tienes una entrada abierta en este club</strong>, hasta
            el {fechaYHora(yaDentro.expires_at)}.{' '}
            <button
              type="button"
              className="enlace-fila"
              onClick={() => {
                cerrarEntrada();
                abrirSuPanel(yaDentro.id);
              }}
            >
              Abrir su panel →
            </button>
          </div>
        ) : (
          <div className="nota">
            Diez caracteres como mínimo, y con fondo:{' '}
            <strong>«Revisión» no es un motivo</strong>.
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={entrar.isPending}>
            {entrar.isPending ? 'Entrando…' : 'Entrar'}
          </button>
          <button type="button" className="btn-plano" onClick={cerrarEntrada}>
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Una persona ──────────────────────────────────────────────────────────
  if (personaAbierta) {
    if (persona.isPending) {
      return <Cargando filas={6} etiqueta="Cargando la persona" />;
    }

    if (persona.isError || !persona.data) {
      return (
        <PanelError
          problema={aProblema(persona.error)}
          reintentar={() => void persona.refetch()}
        />
      );
    }

    const p = persona.data;

    return (
      <section>
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => alAbrirPersona(null)}
          >
            ← Soporte
          </button>
        </div>

        <h1>
          {p.name}{' '}
          {/* La respuesta a «no me llega el correo», arriba y con color. */}
          <span
            className={`pastilla ${p.email_verified ? 'pastilla-bien' : 'pastilla-mal'}`}
          >
            {p.email_verified ? 'Correo verificado' : 'Correo sin verificar'}
          </span>
          {p.is_platform_staff && (
            <span className="pastilla pastilla-info pastilla-left">
              Equipo de Kiap
            </span>
          )}
        </h1>

        <dl className="datos">
          <div>
            <dt>Correo</dt>
            <dd>{p.email}</dd>
          </div>
          <div>
            <dt>Última vez</dt>
            <dd>{fechaYHora(p.last_seen_at)}</dd>
          </div>
          <div>
            <dt>Se registró</dt>
            <dd>{fechaYHora(p.created_at)}</dd>
          </div>
          <div>
            <dt>Idioma</dt>
            <dd>{p.locale ?? '—'}</dd>
          </div>
        </dl>

        <h2 className="seccion">En qué clubes está</h2>

        {p.clubs.length === 0 ? (
          <p className="tenue">
            En ninguno. Si dice que no puede entrar, es esto: una cuenta sin
            membresía activa no abre nada.
          </p>
        ) : (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Aceptó</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {p.clubs.map((m) => (
                  <tr key={`${m.academy_id}-${m.role}`}>
                    <td>{m.academy_name}</td>
                    <td className="tenue">{m.role}</td>
                    <td>
                      <span
                        className={`pastilla ${
                          m.status === 'active'
                            ? 'pastilla-bien'
                            : 'pastilla-left'
                        }`}
                      >
                        {m.status}
                      </span>
                    </td>
                    <td className="tenue">{fechaYHora(m.accepted_at)}</td>
                    <td>
                      {/* Quien llama dice su correo, y de aquí se entra en su club. */}
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => abrirEntrada(m.academy_id)}
                      >
                        Entrar en modo soporte
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h2 className="seccion">De quién es tutora</h2>

        {/*
          Los nombres de los hijos y nada más: ni su ficha médica, ni sus notas, ni
          su asistencia. Soporte necesita saber **a quién cubre** para entender la
          llamada; lo demás no hace falta para eso.
        */}
        {p.students.length === 0 ? (
          <p className="tenue">De nadie.</p>
        ) : (
          <ul className="cubiertos">
            {p.students.map((a) => (
              <li key={a.student_id}>
                <span>
                  <strong>{a.first_name}</strong>{' '}
                  <span className="tenue">· {a.academy_name}</span>
                </span>
                <span className="pastilla pastilla-info">{a.access_level}</span>
              </li>
            ))}
          </ul>
        )}

        <p className="tenue">
          Aquí no hay datos de los hijos más allá del nombre: ni salud, ni
          asistencia, ni grados. Para eso hay que entrar en el club en modo
          soporte, que deja rastro.
        </p>
      </section>
    );
  }

  // ── La pantalla de soporte ───────────────────────────────────────────────
  const vivas = (sesiones.data ?? []).filter((s) => s.active);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Soporte</h1>
        <button type="button" onClick={() => abrirEntrada('')}>
          Entrar en modo soporte
        </button>
      </header>

      <p className="tenue">
        Busca por correo: es lo que dice quien llama, y de ahí sale todo lo
        demás.
      </p>

      <div className="filtros">
        <input
          type="search"
          placeholder="Correo o nombre"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {busqueda.trim().length > 0 && busqueda.trim().length < 3 && (
        <p className="tenue">Escribe al menos tres caracteres.</p>
      )}

      {personas.isPending && busqueda.trim().length >= 3 && (
        <Cargando filas={3} etiqueta="Buscando" />
      )}

      {personas.isError && (
        <PanelError
          problema={aProblema(personas.error)}
          reintentar={() => void personas.refetch()}
        />
      )}

      {personas.data?.length === 0 && (
        <Vacio
          titulo="Nadie con ese correo"
          explicacion="Si acaba de recibir una invitación y todavía no la ha aceptado, aún no tiene cuenta: la invitación vive en el club, no aquí."
        />
      )}

      {personas.data && personas.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Correo</th>
                <th className="num">Clubes</th>
                <th>Última vez</th>
              </tr>
            </thead>
            <tbody>
              {personas.data.map((p) => (
                <tr key={p.user_id}>
                  <td>
                    <button
                      type="button"
                      className="enlace-fila"
                      onClick={() => alAbrirPersona(p.user_id)}
                    >
                      {p.name}
                    </button>
                  </td>
                  <td className="tenue">
                    {p.email}
                    {!p.email_verified && (
                      <span className="pastilla pastilla-mal pastilla-left">
                        Sin verificar
                      </span>
                    )}
                  </td>
                  <td className="num tenue">{p.memberships}</td>
                  <td className="tenue">{fechaYHora(p.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="seccion">Entradas en modo soporte</h2>

      {problema && <PanelError problema={problema} />}

      {sesiones.isPending && <Cargando filas={3} etiqueta="Cargando" />}

      {vivas.length > 0 && (
        <div className="aviso" role="status">
          <strong>
            {vivas.length === 1
              ? 'Hay 1 entrada viva ahora mismo'
              : `Hay ${vivas.length} entradas vivas ahora mismo`}
            .
          </strong>{' '}
          Cada una está viendo los datos de un club de verdad.
        </div>
      )}

      {sesiones.data?.length === 0 && (
        <p className="tenue">Nadie ha entrado en ningún club todavía.</p>
      )}

      {sesiones.data && sesiones.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Club</th>
                <th>Quién</th>
                <th>Por qué</th>
                <th>Cuándo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sesiones.data.map((s) => (
                <tr key={s.id} className={s.active ? 'fila-aviso' : undefined}>
                  <td>{s.academy_name}</td>
                  <td className="tenue">{s.staff_name}</td>
                  <td>{s.reason}</td>
                  <td className="tenue">
                    {fechaYHora(s.started_at)}
                    {s.active && <div>hasta {fechaYHora(s.expires_at)}</div>}
                    {s.write_enabled_at && (
                      <div>
                        con escritura desde {fechaYHora(s.write_enabled_at)}
                      </div>
                    )}
                  </td>
                  <td>
                    {/*
                      Volver a entrar con el mismo club y motivo, que se pueden cambiar
                      antes de pulsar «Entrar». Es otra entrada: el club recibe otro aviso.
                    */}
                    {!s.active && s.staff_user_id === yo.user.id && (
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => abrirEntrada(s.academy_id, s.reason)}
                      >
                        Volver a entrar
                      </button>
                    )}
                    {s.active && (
                      <span className="tutor-acciones" style={{ margin: 0 }}>
                        {/* Solo la tuya: el motivo que leyó el club es el de quien entró. */}
                        {s.staff_user_id === yo.user.id && (
                          <button
                            type="button"
                            onClick={() => abrirSuPanel(s.id)}
                          >
                            Abrir su panel
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={salir.isPending}
                          onClick={() => salir.mutate(s.id)}
                        >
                          Salir
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
